// src/pages/AdminServicesPage.js
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Form, Modal, Spinner, Table,
} from "react-bootstrap";
import api from "../api";

const EMPTY_FORM = { category: "", label: "", code: "" };

export default function AdminServicesPage() {
  const [services, setServices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [actionError, setActionError] = useState("");

  const [modal, setModal]         = useState(null); // "add" | "edit"
  const [target, setTarget]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [toggling, setToggling]   = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/admin/service-types");
      setServices(res.data.items);
    } catch {
      setError("Failed to load service types.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function field(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  function openAdd() {
    setForm(EMPTY_FORM);
    setActionError("");
    setTarget(null);
    setModal("add");
  }

  function openEdit(st) {
    setForm({ category: st.category, label: st.label, code: st.code });
    setActionError("");
    setTarget(st);
    setModal("edit");
  }

  function closeModal() {
    setModal(null);
    setActionError("");
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.category.trim()) { setActionError("Category is required."); return; }
    setSaving(true);
    setActionError("");
    try {
      await api.post("/admin/service-types", {
        category: form.category.trim(),
        label: form.label.trim(),
        code: form.code.trim(),
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to create service type.");
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(e) {
    e.preventDefault();
    if (!form.category.trim()) { setActionError("Category is required."); return; }
    setSaving(true);
    setActionError("");
    try {
      await api.put(`/admin/service-types/${target.id}`, {
        category: form.category.trim(),
        label: form.label.trim(),
        code: form.code.trim(),
      });
      closeModal();
      await load();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to update service type.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(st) {
    setToggling(st.id);
    try {
      await api.put(`/admin/service-types/${st.id}`, { active: !st.active });
      await load();
    } catch {
      setError("Failed to update service status.");
    } finally {
      setToggling(null);
    }
  }

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex align-items-center mb-4 gap-3">
        <Link to="/admin" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1" />Back
        </Link>
        <h3 className="mb-0">
          <i className="bi bi-clipboard2-check-fill me-2" />
          Services
        </h3>
        <Button size="sm" className="ms-auto" onClick={openAdd}>
          <i className="bi bi-plus-lg me-1" />Add Service
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th>Category</th>
            <th>Label</th>
            <th>Code</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {services.map((st) => {
            const isBusy = toggling === st.id;
            return (
              <tr key={st.id} className={!st.active ? "table-secondary" : ""}>
                <td className="align-middle fw-semibold">{st.category}</td>
                <td className="align-middle text-muted">{st.label || <span className="fst-italic">—</span>}</td>
                <td className="align-middle text-muted">{st.code || <span className="fst-italic">—</span>}</td>
                <td className="align-middle">
                  {st.active
                    ? <Badge bg="success">Active</Badge>
                    : <Badge bg="secondary">Disabled</Badge>}
                </td>
                <td className="align-middle">
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-primary" onClick={() => openEdit(st)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant={st.active ? "outline-danger" : "outline-success"}
                      disabled={isBusy}
                      onClick={() => handleToggleActive(st)}
                    >
                      {isBusy
                        ? <Spinner animation="border" size="sm" />
                        : st.active ? "Disable" : "Enable"}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Add Modal */}
      <Modal show={modal === "add"} onHide={closeModal} centered>
        <Form onSubmit={handleAdd}>
          <Modal.Header closeButton>
            <Modal.Title>Add Service Type</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Category <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.category} onChange={field("category")} placeholder="e.g. Bed, Food, Gas" autoFocus />
              <Form.Text className="text-muted">This is the primary name shown on client service forms.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Label</Form.Label>
              <Form.Control value={form.label} onChange={field("label")} placeholder="Optional sub-label" />
            </Form.Group>
            <Form.Group>
              <Form.Label>Code</Form.Label>
              <Form.Control value={form.code} onChange={field("code")} placeholder="Optional short code" />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Create Service"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal show={modal === "edit"} onHide={closeModal} centered>
        <Form onSubmit={handleEdit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Service Type</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Category <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.category} onChange={field("category")} autoFocus />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Label</Form.Label>
              <Form.Control value={form.label} onChange={field("label")} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Code</Form.Label>
              <Form.Control value={form.code} onChange={field("code")} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
